import { Link, Rule, ui, WORK_NAV } from '../ui';
import { useApi } from '../api';
import { Screen, Table } from './shared';

interface Overview {
  queues: Record<string, number>;
  figures: { lines: number };
}

/**
 * Staff overview.
 *
 * **The unbalanced-transaction alarm was the first thing on this page and is
 * gone.** A double-entry transaction whose debits and credits disagreed meant
 * money was wrong somewhere, and nothing else here mattered until it was zero.
 * There are no journals to disagree: Creem is the record of money, and the
 * check that replaces it is a person comparing this store against Creem's
 * dashboard. `GET /admin/overview` still sends `unbalanced: 0` so an older
 * client reads "nothing is wrong" rather than throwing, and this page no longer
 * looks at it.
 *
 * **It also has links now.** Every admin screen was a registered route that
 * nothing linked to, so reaching the curation queue meant typing its path. A
 * page nobody can navigate to is a page nobody uses, and the counts above it
 * were reporting on queues the reader had no way to open.
 *
 * **And this table is no longer the only way between them (#111).** It was, and
 * that is what the task is about: reaching the ledger from the people table
 * meant coming back here first, on every hop, because the panel's navigation
 * lived on one of its eleven pages. `WORK_NAV.admin` is the rail down the side
 * of all of them now, and the table below is derived from it — see `SCREENS`.
 */

/**
 * What each screen is for, in the words somebody choosing between them needs.
 *
 * Prose only. The hrefs and the labels come from `WORK_NAV.admin`, and keeping
 * the two apart is the point of #111's second half: this used to be a
 * hand-written list of eight `{ href, label, note }` rows, which is to say a
 * second register of admin routes maintained beside the router and swept by
 * nothing. `/admin/agreements` was what that cost — a registered route with a
 * finished screen behind it that no page in the product linked to, because
 * adding it to the router did not add it here and nothing could tell. That
 * screen is gone; the way of avoiding it is not.
 *
 * Keyed by href rather than by position, so reordering the rail cannot silently
 * move a description onto the wrong screen. A key with no rail entry is simply
 * unused; a rail entry with no key here renders an empty cell, which is the
 * mild failure of the two and is why this is not the list that decides what the
 * table holds.
 */
const NOTES: Readonly<Record<string, string>> = {
  // `/admin/publishing` was here — the pitch inbox — and went with the form
  // that filled it (#142). `/admin/studios`, `/admin/agreements` and
  // `/admin/contracts` were here too, and went with the studios (#144).
  '/admin/creators': 'Applications and suspensions',
  '/admin/people': 'Everyone on the platform — players and creators',
  '/admin/games': 'The whole catalogue, in every state — and where a title is added',
  '/admin/gifts': 'Give a game to somebody without them paying — and take one back',
  '/admin/tokens': 'The CI credentials a build is pushed with — mint one, revoke one',
  '/admin/refunds': 'Refund requests waiting on a decision — approving one sends the money back',
};

/**
 * Every admin screen, in the order the rail puts them.
 *
 * Derived rather than written out, for the reason `rail.ts` derives the
 * developers' menu from the same constant: a second copy of these hrefs is a
 * second place for a renamed route to leave a dead link, and it would be the
 * copy nothing checks. `apps/app/src/ui/test/site-chrome.test.ts` sweeps
 * `WORK_NAV.admin` against the router in both directions, so this table
 * inherits that for free — and the order is the rail's, which means the page
 * and the navigation beside it read the same way down.
 *
 * `/admin` is filtered out because it is this page. A row linking to the screen
 * the reader is already on is a click that does nothing, and the rail marks it
 * as current a few centimetres to the left.
 */
const SCREENS: readonly { href: string; label: string; note: string }[] = WORK_NAV.admin
  .flatMap((section) => section.items)
  .filter((item) => item.href !== '/admin')
  .map((item) => ({ href: item.href, label: item.label, note: NOTES[item.href] ?? '' }));

export function AdminRoute(): React.ReactElement {
  const query = useApi<Overview>(['admin', 'overview'], '/admin/overview');

  return (
    <Screen title="Admin" query={query}>
      {query.data ? (
        <>
          <Table head={['Queue', 'Waiting']}>
            {Object.entries(query.data.queues).map(([name, count]) => (
              <tr key={name}>
                <td>{name.replace(/([A-Z])/g, ' $1').toLowerCase()}</td>
                <td className={ui.numeric ?? ''}>{count}</td>
              </tr>
            ))}
          </Table>

          <Rule />

          <Table head={['Screen', 'What it is for']}>
            {SCREENS.map((screen) => (
              <tr key={screen.href}>
                <td>
                  <Link href={screen.href}>{screen.label}</Link>
                </td>
                <td>{screen.note}</td>
              </tr>
            ))}

            {/*
              A `Developer portal` row was here, drawn when the administrator
              also held a studio, and went with the portal (#144). Every screen
              this panel can reach is now every screen there is.
            */}
          </Table>
        </>
      ) : null}
    </Screen>
  );
}

/*
  `AdminLedgerRoute` and its `LedgerRow` shape were here, and the ledger removal
  took them.

  It listed every account's balance, summed from entries on each request because
  hard rule 2 forbade a stored running total anywhere and this was the page
  somebody reconciled a bank statement against. There are no accounts: GOODBUILD
  sells its own games through Creem, and Creem's dashboard is the record of
  money.

  `GET /v1/admin/ledger` went with it, as did the `Ledger` entry in the staff
  rail. The three tables still hold every journal ever written and nothing
  serves them.
*/
